import { Secret, TOTP } from 'otpauth';
import React from 'react';

export type HashAlgorithm = 'SHA1' | 'SHA256' | 'SHA512';

export interface TOTPOptions {
  readonly digits?: number;
  readonly period?: number;
  readonly algorithm?: HashAlgorithm;
  readonly issuer?: string;
  readonly accountName?: string;
}

export interface TOTPResult {
  readonly secret: string;
  readonly uri: string;
  readonly digits: number;
  readonly period: number;
  readonly algorithm: HashAlgorithm;

  validate(code: string): boolean;
}

const defaultOptions: TOTPOptions = {
  digits: 6,
  period: 30,
  algorithm: 'SHA1',
  issuer: 'Caliza',
  accountName: 'user@example.com',
};

const ACCEPTABLE_CHARACTERS = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ234567';

const generateSecret = (): Secret => {
  const cryptoObj = globalThis.crypto;
  if (!cryptoObj || typeof cryptoObj.getRandomValues !== 'function') {
    throw new Error('crypto.getRandomValues not available in current environment.');
  }

  const buffer = new Uint8Array(20);
  cryptoObj.getRandomValues(buffer);

  const out = Array.from(buffer).map((chr: number): number => {
    const index = chr % ACCEPTABLE_CHARACTERS.length;
    return ACCEPTABLE_CHARACTERS.charCodeAt(index);
  });

  return new Secret({ buffer: Uint8Array.from(out) });
};

export const useTOTP = (options: Partial<TOTPOptions> = {}): TOTPResult => {
  const secret = React.useMemo((): Secret => generateSecret(), []);
  const computedOptions = React.useMemo(
    (): TOTPOptions => ({ ...defaultOptions, ...options }),
    [options],
  );

  const totp = React.useMemo((): TOTP => {
    return new TOTP({
      secret: secret,
      digits: computedOptions.digits,
      period: computedOptions.period,
      algorithm: computedOptions.algorithm,
      issuer: computedOptions.issuer,
      label: computedOptions.accountName,
    });
  }, [
    computedOptions.accountName,
    computedOptions.algorithm,
    computedOptions.digits,
    computedOptions.issuer,
    computedOptions.period,
    secret,
  ]);

  return {
    secret: secret.base32,
    uri: totp.toString(),
    algorithm: totp.algorithm as HashAlgorithm,
    digits: totp.digits,
    period: totp.period,
    validate: (code: string): boolean => {
      return totp.validate({ token: code, window: 1 }) !== null;
    },
  };
};
